// In-memory batching with a bounded localStorage spill. All failures are
// swallowed — analytics must never break the app — but every drop is
// counted and reported via _meta on the next successful flush, EXCEPT
// deliberate flag-off / kill-switch discards: those are a policy decision,
// not data loss, so they're never counted. That applies both at enqueue
// time (gateDecision() === 'drop' before an event is ever buffered) and at
// flush time (events that were buffered/spilled while the cache was cold
// — decision 'hold' — and the flag has since resolved to off).
import { buildEnvelope, stampFlags, AnalyticsEnvelope } from './envelope';
import { gateDecision } from './gates';
import { post, postOnUnload, FlushBody } from './transport';

export const SPILL_LS_KEY = 'analytics:spill';
const FLUSH_AT = 20;
const FLUSH_MS = 10_000;
const SPILL_CAP = 200;

let buffer: AnalyticsEnvelope[] = [];
let droppedSinceLast = 0;
let timer: ReturnType<typeof setInterval> | null = null;
let flushing = false;

function ensureTimer(): void {
  if (timer) return;
  timer = setInterval(() => void flush(), FLUSH_MS);
}

function readSpill(): AnalyticsEnvelope[] {
  try {
    const raw = localStorage.getItem(SPILL_LS_KEY);
    return raw ? (JSON.parse(raw) as AnalyticsEnvelope[]) : [];
  } catch {
    return [];
  }
}

function writeSpill(events: AnalyticsEnvelope[]): void {
  try {
    const trimmed = events.slice(-SPILL_CAP);
    droppedSinceLast += events.length - trimmed.length;
    localStorage.setItem(SPILL_LS_KEY, JSON.stringify(trimmed));
  } catch {
    droppedSinceLast += events.length;
  }
}

// Empties buffered + spilled events without sending or counting them as
// data loss. Called when a flush attempt finds the gate has resolved to
// 'drop' (flag off / kill switch) — those events were only ever held
// because the cache was cold at enqueue time (decision 'hold'), and now
// that the answer is in, the answer is "don't send". Without this, a
// cold-cache enqueue followed by the flag settling off would leave the
// events in `buffer` for the rest of the session (unbounded growth, no
// terminal state) since flush() used to bail before touching it.
function discardBuffered(): void {
  buffer = [];
  try {
    localStorage.removeItem(SPILL_LS_KEY);
  } catch {
    /* swallow */
  }
}

function takeBatch(): AnalyticsEnvelope[] {
  const spilled = readSpill();
  try {
    localStorage.removeItem(SPILL_LS_KEY);
  } catch {
    /* swallow */
  }
  // Stamp events whose flag map was cold at enqueue time (assignments are
  // sticky server-side, so a seconds-later stamp is equivalent).
  const stamp = stampFlags() ?? {};
  const batch = [...spilled, ...buffer].map((event) =>
    event.feature_flags === null ? { ...event, feature_flags: stamp } : event,
  );
  buffer = [];
  return batch;
}

export function enqueueAnalyticsEvent(
  name: string,
  payload: Record<string, unknown>,
): void {
  try {
    const decision = gateDecision();
    if (decision === 'drop') return; // deliberate: not counted as data loss
    buffer.push(buildEnvelope(name, payload));
    ensureTimer();
    if (buffer.length + readSpill().length >= FLUSH_AT) void flush();
  } catch {
    droppedSinceLast += 1;
  }
}

export async function flush(): Promise<void> {
  if (flushing) return;
  const decision = gateDecision();
  if (decision === 'hold') return; // cache still cold — keep buffering, try again later
  if (decision === 'drop') {
    discardBuffered(); // flag resolved off after a cold-cache hold — see discardBuffered()
    return;
  }
  const batch = takeBatch();
  if (batch.length === 0) return;
  flushing = true;
  try {
    const body: FlushBody = { events: batch.slice(0, 50) };
    if (droppedSinceLast > 0)
      body._meta = { dropped_since_last: droppedSinceLast };
    const remainder = batch.slice(50);
    const ok = await post(body);
    if (ok) {
      droppedSinceLast = 0;
      if (remainder.length) {
        buffer = [...remainder, ...buffer];
        void flush();
      }
    } else {
      writeSpill([...batch]);
    }
  } finally {
    flushing = false;
  }
}

export function flushAnalytics(opts: { unload?: boolean } = {}): void {
  if (!opts.unload) {
    void flush();
    return;
  }
  const decision = gateDecision();
  if (decision === 'hold') return; // cache still cold — keep buffering, try again later
  if (decision === 'drop') {
    discardBuffered(); // flag resolved off after a cold-cache hold — see discardBuffered()
    return;
  }
  const batch = takeBatch();
  if (batch.length === 0) return;
  const body: FlushBody = { events: batch };
  if (droppedSinceLast > 0)
    body._meta = { dropped_since_last: droppedSinceLast };
  // Reset BEFORE postOnUnload, not after: postOnUnload's writeSpill() (on a
  // byte-cap overflow it can't even keep, or a full transport miss) may
  // itself increment droppedSinceLast for the *next* session's flush to
  // report. Resetting first lets that increment survive; resetting last
  // would immediately wipe it back to 0.
  droppedSinceLast = 0;
  const overflow = postOnUnload(body);
  if (overflow.length) writeSpill(overflow);
}

export function initAnalyticsQueue(): void {
  ensureTimer();
  if (readSpill().length > 0) void flush(); // replay a previous session's spill
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushAnalytics({ unload: true });
  });
  window.addEventListener('pagehide', () => flushAnalytics({ unload: true }));
}

export function __resetQueueForTests(): void {
  buffer = [];
  droppedSinceLast = 0;
  if (timer) clearInterval(timer);
  timer = null;
  flushing = false;
}
